import { apiClient } from '../config/api'
import {
  AttachZoneInput,
  VehicleControlCenter,
  VehicleSource,
  ZoneOption,
  ZoneType
} from './vehicleControlCenterTypes'

const basePath = (source: VehicleSource, id: number) =>
  `/vehicles/${source}/${id}/control-center`

const rowsFrom = (value: unknown): ZoneOption[] => {
  if (Array.isArray(value)) return value as ZoneOption[]
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    for (const key of ['items', 'data', 'geofences', 'corridors']) {
      if (Array.isArray(record[key])) return record[key] as ZoneOption[]
    }
  }
  return []
}

export const vehicleControlCenterService = {
  async get(source: VehicleSource, id: number): Promise<VehicleControlCenter> {
    const response = await apiClient.get(basePath(source, id))
    const data = (response.data || {}) as Record<string, any>
    const alertRows = Array.isArray(data.alerts)
      ? data.alerts
      : [...(data.alerts?.active || []), ...(data.alerts?.history || [])]
    return {
      vehicle: data.vehicle || {},
      tracking: data.tracking || null,
      balises: Array.isArray(data.balises) ? data.balises : [],
      fleets: Array.isArray(data.fleets) ? data.fleets : [],
      missions: Array.isArray(data.missions) ? data.missions : [],
      geofences: Array.isArray(data.geofences) ? data.geofences : [],
      corridors: Array.isArray(data.corridors) ? data.corridors : [],
      alert_rules: Array.isArray(data.alert_rules) ? data.alert_rules : [],
      alerts: alertRows,
      zone_state: data.zone_state || {},
      summary: {
        geofence_count: Number(data.summary?.geofence_count || 0),
        corridor_count: Number(data.summary?.corridor_count || 0),
        enabled_rule_count: Number(data.summary?.enabled_rule_count || 0),
        unresolved_alert_count: Number(data.summary?.unresolved_alert_count || 0),
        configured_but_untracked: Boolean(data.summary?.configured_but_untracked)
      },
      permissions: data.permissions || {}
    }
  },

  async listZones(type: ZoneType): Promise<ZoneOption[]> {
    const response = await apiClient.get(type === 'geofence' ? '/geofences' : '/corridors')
    return rowsFrom(response.data)
  },

  async attachZone(source: VehicleSource, id: number, input: AttachZoneInput): Promise<void> {
    await apiClient.post(`${basePath(source, id)}/zones`, input)
  },

  async detachZone(source: VehicleSource, id: number, type: ZoneType, zoneId: string | number): Promise<void> {
    await apiClient.delete(`${basePath(source, id)}/zones/${type}/${encodeURIComponent(String(zoneId))}`)
  },

  async setRuleEnabled(ruleId: number, enabled: boolean): Promise<void> {
    await apiClient.put(`/alert-rules/${ruleId}`, { is_active: enabled, enabled })
  },

  async transitionAlert(alertId: number, action: 'acknowledge' | 'resolve'): Promise<void> {
    await apiClient.post(`/alerts/${alertId}/${action}`, {})
  }
}
